import { PrincipalIdSchema } from "@core/domains/identity";
import { ulid, uuid } from "@core/foundation/id";
import {
  createModel,
  createModelId,
  createModelSchemas,
} from "@core/foundation/model";
import { singleline } from "@core/foundation/string";
import { z } from "@core/zod";

import { ID_PREFIX } from "./shared/id-prefix.ts";

const { generateId: generateProjectId, idSchema: ProjectIdSchema } =
  createModelId({ digestFn: ulid, idPrefix: ID_PREFIX.PROJECT });

export { ProjectIdSchema };

export const Project = createModel({
  create: (item, schema) => {
    return {
      ...schema.decode(item),
      id: generateProjectId(),
      memorySnapshotEtag: uuid(),
    };
  },
  schemas: createModelSchemas({
    base: z.object(
      {
        apiKeyHash: z.string().optional(),
        connectedGithubRepoIds: z.array(z.int().nonnegative()),
        id: ProjectIdSchema,
        memorySnapshotEtag: z.string(),
        name: z.string(),
        principalId: PrincipalIdSchema,
      },
      {
        apiKeyHash: "The digest of the project's current API key.",
        connectedGithubRepoIds:
          "The GitHub ids of the repos the project connects.",
        id: "The unique identifier for the project.",
        memorySnapshotEtag: singleline`
          The tag of the project's memory snapshot, rerolled by every change to
          its memories.
        `,
        name: "The project's name.",
        principalId: "The id of the principal the project belongs to.",
      },
    ),
    defaults: { connectedGithubRepoIds: [] },
    derive: {
      create: schema =>
        schema.omit({ apiKeyHash: true, id: true, memorySnapshotEtag: true }),
      recovery: schema =>
        schema.extend(
          {
            memorySnapshotEtag: schema.shape.memorySnapshotEtag.default(() =>
              uuid(),
            ),
          },
          { memorySnapshotEtag: schema.descriptions.memorySnapshotEtag },
        ),
      update: schema =>
        schema
          .pick({ apiKeyHash: true, connectedGithubRepoIds: true })
          .partial(),
    },
  }),
  update: (item, update, schema) => {
    return { ...item, ...schema.decode(update) };
  },
});

export interface Project extends z.infer<typeof Project.schema> {}
